// Narrow the `progress` blob of an in-flight analysis (AnalysisDetail.progress,
// passthrough from Jarts Redis) into typed values for the run-progress screen.
// Defensive throughout (the blob is `Record<string, unknown> | null`): every
// field is type-checked and clamped so a malformed/partial poll never throws.
//
// Shape (PENDING/IN_PROGRESS): { progress: 0-100, phase, runs: [{ key, provider,
// model, status, done, total }], ... }. On terminal status it's null, so callers
// branch on the analysis status, not on this.

export type RunLegStatus = "QUEUED" | "IN_PROGRESS" | "DONE" | "FAILED";

export interface RunLeg {
  /** Stable per-provider key, e.g. "OPENAI:gpt-4.1-mini". */
  key: string;
  /** Provider family, e.g. "OPENAI" / "GEMINI" / "PERPLEXITY". */
  provider: string;
  /** Model id, e.g. "gpt-4.1-mini". */
  model: string;
  status: RunLegStatus;
  /** Questions answered by this provider so far. */
  done: number;
  /** Total questions for this provider. */
  total: number;
}

export interface RunProgress {
  /** Overall completion percent, clamped 0-100. */
  overall: number;
  /** Worker pipeline stage ("setup" / "runs" / "finalize"), if present. */
  phase: string | null;
  legs: RunLeg[];
}

const STATUSES: RunLegStatus[] = ["QUEUED", "IN_PROGRESS", "DONE", "FAILED"];

function asInt(v: unknown): number {
  return typeof v === "number" && Number.isFinite(v) ? Math.trunc(v) : 0;
}

function asStr(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function asStatus(v: unknown): RunLegStatus {
  const s = asStr(v).toUpperCase();
  return (STATUSES as string[]).includes(s) ? (s as RunLegStatus) : "QUEUED";
}

function parseLeg(raw: unknown): RunLeg | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const provider = asStr(r.provider).toUpperCase();
  const model = asStr(r.model);
  const key = asStr(r.key) || (provider && model ? `${provider}:${model}` : provider);
  if (!key) return null;
  const total = Math.max(0, asInt(r.total));
  const done = Math.min(Math.max(0, asInt(r.done)), total || Number.MAX_SAFE_INTEGER);
  return { key, provider, model, status: asStatus(r.status), done, total };
}

/** Parse the in-flight progress blob. Returns an empty shape (overall 0, no
 *  legs) when the blob is absent - e.g. a freshly-PENDING run before Redis is
 *  read - so the UI can render a "Queued" state without special-casing null. */
export function parseRunProgress(progress: unknown): RunProgress {
  if (!progress || typeof progress !== "object") {
    return { overall: 0, phase: null, legs: [] };
  }
  const p = progress as Record<string, unknown>;
  // The per-provider legs live at `payload.runs` (Jarts seeds them there at
  // trigger and merges live done/total ticks into the same place - see
  // jarts/app/jobs.py `_merge_runs_for_job`, which writes via update_job's
  // `patch` that nests under `payload`). Fall back to a top-level `runs` for
  // forward-compat if that shape ever changes.
  const payload = (p.payload && typeof p.payload === "object" ? p.payload : {}) as Record<
    string,
    unknown
  >;
  const rawLegs = Array.isArray(payload.runs)
    ? payload.runs
    : Array.isArray(p.runs)
      ? p.runs
      : [];
  const legs = rawLegs.map(parseLeg).filter((l): l is RunLeg => l !== null);
  const phase = typeof p.phase === "string" && p.phase ? p.phase : null;

  // Prefer deriving the headline percent from the actual answered counts so the
  // bar tracks the per-provider chips exactly (0/30 -> 0%). The backend's
  // top-level `progress` floors at ~45 during the setup phase before any
  // question is answered, which reads as "45% but 0/10 done" - confusing. Only
  // fall back to that backend value when no legs with totals exist yet (the
  // brief pre-seed window).
  const withTotals = legs.filter((l) => l.total > 0);
  const answered = withTotals.reduce((s, l) => s + Math.min(l.done, l.total), 0);
  const totalQ = withTotals.reduce((s, l) => s + l.total, 0);
  const overall =
    totalQ > 0
      ? Math.round((100 * answered) / totalQ)
      : Math.min(100, Math.max(0, asInt(p.progress)));

  return { overall, phase, legs };
}
